import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

const BackgroundRoot = styled(Box)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  overflow: 'hidden',
  zIndex: 0,
}));

const ImageLayer = styled('div')<{ $url: string; $mode: 'light' | 'dark' }>(({ $url, $mode, theme }) => ({
  position: 'absolute',
  inset: '-10%',
  backgroundImage: `url(${$url})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  transform: 'translate3d(0,0,0) scale(1.05)',
  transition: 'opacity 220ms ease, transform 0.2s ease-out',
  willChange: 'transform, opacity',
  filter: 'saturate(105%) contrast(102%)',
  // Only visible in light theme
  opacity: theme.palette.mode === $mode ? 1 : 0,
  pointerEvents: 'none',
}));

const SkyGradient = styled('div')(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  background:
    theme.palette.mode === 'dark'
      ? 'linear-gradient(to bottom, rgba(20,24,34,0.7) 0%, rgba(20,24,34,0.25) 40%, rgba(20,24,34,0.7) 100%)'
      : 'linear-gradient(to bottom, rgba(135,206,250,0.35) 0%, rgba(255,255,255,0.2) 40%, rgba(255,255,255,0.7) 100%)',
  pointerEvents: 'none',
  transition: 'background 220ms ease',
}));

const MistOverlay = styled('div')(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  background:
    'radial-gradient(60% 40% at 50% 30%, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.35) 40%, rgba(255,255,255,0) 70%)',
  filter: 'blur(20px)',
  opacity: theme.palette.mode === 'dark' ? 0.18 : 0.28,
  pointerEvents: 'none',
  transition: 'opacity 220ms ease',
}));

export interface CloudyBackgroundProps {
  imageUrl?: string; // backward compatibility (treated as light)
  imageUrlLight?: string;
  imageUrlDark?: string;
  parallax?: boolean;
}

const DEFAULT_IMAGE =
  'https://images.unsplash.com/photo-1501785888041-af3ef285b470?q=80&w=2000&auto=format&fit=crop';

const CloudyBackground: React.FC<CloudyBackgroundProps> = ({ imageUrl = DEFAULT_IMAGE, imageUrlLight, imageUrlDark, parallax = true }) => {
  const lightRef = useRef<HTMLDivElement>(null);
  const darkRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!parallax) return;
    const nodes: (HTMLDivElement | null)[] = [lightRef.current, darkRef.current];

    const handleMove = (e: MouseEvent) => {
      const { innerWidth, innerHeight } = window;
      const x = (e.clientX / innerWidth - 0.5) * 10; // -5 to 5
      const y = (e.clientY / innerHeight - 0.5) * 10;
      nodes.forEach(node => {
        if (node) node.style.transform = `translate3d(${x * -1}px, ${y * -1}px, 0) scale(1.06)`;
      });
    };

    window.addEventListener('mousemove', handleMove);
    return () => window.removeEventListener('mousemove', handleMove);
  }, [parallax]);

  return (
    <BackgroundRoot>
      <ImageLayer ref={lightRef} $url={imageUrlLight ?? imageUrl} $mode="light" />
      { (imageUrlDark) && (
        <ImageLayer ref={darkRef} $url={imageUrlDark} $mode="dark" />
      )}
      <SkyGradient />
      <MistOverlay />
    </BackgroundRoot>
  );
};

export default CloudyBackground;


